// AnalyticsProvider — wires up all analytics hooks at the app root.
// Each module respects the analytics config feature flags.
// This component renders no DOM — it only activates side-effect hooks.
//
// Hooks must be called unconditionally per Rules of Hooks.
// We use separate null-rendering sub-components so each hook is always called
// at a stable position in its own component tree, with mounting controlled by
// the config flags.

import { type ReactNode } from "react";
import { getAnalyticsConfig } from "@/lib/analytics-config";

// Device & connection hooks
import {
  useDeviceTracking,
  useConnectionQuality,
  usePhoneInteraction,
  usePWATracking,
} from "@/hooks/use-device-tracking";

// Browse behavior hooks
import { useScrollDepth, useTimeOnPage } from "@/hooks/use-browse-tracking";

// Engagement hooks
import { useExitIntent } from "@/hooks/use-exit-intent";
import { useAttribution } from "@/hooks/use-attribution";
import { useCustomerJourney } from "@/hooks/use-customer-journey";
import { useReturnVisitor } from "@/hooks/use-repeat-visit-tracking";

// Click/heatmap
import { useClickTracking, useScrollHeatmap } from "@/hooks/use-click-tracking";

// ---------------------------------------------------------------------------
// One null-component per hook group so each hook is always called at a stable
// position in its own component tree.
// ---------------------------------------------------------------------------

function DeviceTrackingMount() {
  useDeviceTracking();
  return null;
}

function PWATrackingMount() {
  usePWATracking();
  return null;
}

function PhoneInteractionMount() {
  usePhoneInteraction();
  return null;
}

function BrowseTrackingMount() {
  useScrollDepth();
  useTimeOnPage("global");
  return null;
}

function ExitIntentMount() {
  useExitIntent();
  return null;
}

function AttributionMount() {
  useAttribution();
  return null;
}

function CustomerJourneyMount() {
  useCustomerJourney();
  return null;
}

function ReturnVisitorMount() {
  useReturnVisitor();
  return null;
}

function ClickTrackingMount() {
  useClickTracking();
  useScrollHeatmap();
  return null;
}

function ConnectionQualityMount() {
  useConnectionQuality();
  return null;
}

function AnalyticsHooks() {
  const config = getAnalyticsConfig();

  return (
    <>
      {config.deviceTracking && <DeviceTrackingMount />}
      {config.pwaTracking && <PWATrackingMount />}
      {config.phoneInteraction && <PhoneInteractionMount />}
      {config.browse && <BrowseTrackingMount />}
      {config.exitIntent && <ExitIntentMount />}
      {config.attribution && <AttributionMount />}
      {config.customerJourney && <CustomerJourneyMount />}
      {config.repeatVisits && <ReturnVisitorMount />}
      {config.clickTracking && <ClickTrackingMount />}
      {config.connectionQuality && <ConnectionQualityMount />}
    </>
  );
}

interface AnalyticsProviderProps {
  readonly children: ReactNode;
}

export function AnalyticsProvider({ children }: AnalyticsProviderProps) {
  return (
    <>
      <AnalyticsHooks />
      {children}
    </>
  );
}
